import React, { memo, useMemo } from 'react';

import {
  useCampaignCardScale,
  useCampaignCardStyles,
  useCampaignDataAttributes,
  useCampaignState,
} from 'features/campaigns';
import StringMapper from 'shared/components/StringMapper';
import { useTrueMobile } from 'shared/hooks/useTrueMobile';
import { useViewMode } from 'store/app/appStore';
import { usePrizeFundFormatting } from '../../../hooks/data/usePrizeFundFormatting';
import { CampaignBadge } from './CampaignBadge';

import type { PrizeFundCampaignStyles } from './css/base/PrizeFundCampaign';
import originalHasManyStyles from './css/hasManyStyles.module.css';
import { clsx } from 'clsx';

// Accepted `variant` values: 'card' (campaign card), 'panel' (section panel), 'prize-pool' (carousel topper).
const CAROUSEL_VARIANTS = ['prize-pool'] as const;
const isCarouselVariant = (variant?: string) =>
  CAROUSEL_VARIANTS.includes(variant as (typeof CAROUSEL_VARIANTS)[number]);

interface PrizeFundCampaignProps {
  amount: string | number;
  currency: string;
  variant?: string;
}

interface ScaleSettings {
  maxWidth?: number;
  horizontalPadding?: number;
  recalculateKey?: string | number | boolean;
  disabled?: boolean;

  getContainerWidth?: () => number;
}

// Reads a px CSS custom property off :root, resolved for the active media query. Used for the
// amount budgets that mirror a responsive container width (--campaign-prize-scale-budget-*).
const readPxVar = (name: string, fallback: number): number =>
  parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || fallback;

const getScaleSettings = ({
  variant,
  hasMany,
  isMobile,
  isCollapsed,
  viewMode,
}: {
  variant?: string;
  hasMany: boolean;
  isMobile: boolean;
  isCollapsed: boolean;
  viewMode?: string;
}): ScaleSettings => {
  if (isCarouselVariant(variant)) {
    return {
      maxWidth: isMobile ? 200 : 300,
      getContainerWidth: () => {
        const isLandscape = window.matchMedia('(orientation: landscape)').matches;
        if (isLandscape && !isMobile) return 300;
        // The topper width is a CSS value that shrinks on narrow screens; read the matching amount
        // budget from CSS (defined next to the topper width) so the two can't drift apart.
        return readPxVar('--campaign-prize-scale-budget-topper-mobile', 200);
      },
    };
  }

  if (variant === 'panel') {
    return {
      maxWidth: isMobile ? 300 : 420,
    };
  }

  if (hasMany) {
    if (isMobile) {
      // Collapsed and expanded cards have separate widths (only the collapsed one shrinks on narrow
      // screens), so each reads its own amount budget from CSS instead of hardcoding the breakpoints
      // here. getContainerWidth is re-called on every re-measure (resize/orientation), so the active
      // value is always used.
      const budgetVar = isCollapsed
        ? '--campaign-prize-scale-budget-collapsed-card-mobile'
        : '--campaign-prize-scale-budget-expanded-card-mobile';
      return {
        recalculateKey: isCollapsed,
        getContainerWidth: () => readPxVar(budgetVar, 210),
      };
    }
    return {
      maxWidth: isCollapsed ? 300 : 400,
      recalculateKey: isCollapsed,
    };
  }

  return {
    recalculateKey: `${viewMode}-${isCollapsed}`,
  };
};

const PrizeFundCampaign: React.FC<PrizeFundCampaignProps> = ({ amount, currency, variant }) => {
  const { formattedAmount } = usePrizeFundFormatting(amount);
  const { isCollapsed } = useCampaignState();
  const { getPrizeFundCampaignStyles } = useCampaignCardStyles();
  const dataAttributes = useCampaignDataAttributes({
    additionalProps: { 'data-variant': variant }
  });
  const hasMany = dataAttributes?.['data-has-many']; //no hasMany for Panel
  const hasManyStyles = originalHasManyStyles as unknown as PrizeFundCampaignStyles;
  const prizeFundCampaignStyles = getPrizeFundCampaignStyles();
  const showHasManyClass = variant !== 'panel' && hasMany;
  const isMobile = useTrueMobile();
  const viewMode = useViewMode();

  const scaleSettings = useMemo(
    () => getScaleSettings({ variant, hasMany, isMobile, isCollapsed, viewMode }),
    [variant, hasMany, isMobile, isCollapsed, viewMode],
  );

  const { titleRef, scale, scaledWidth } = useCampaignCardScale(formattedAmount, {
    ...scaleSettings,
  });
  return (
    <div
      className={clsx(prizeFundCampaignStyles.prizeContainer, showHasManyClass && hasManyStyles.prizeContainer)}
      {...dataAttributes}
      ref={titleRef as React.RefObject<HTMLDivElement>}
      style={{
        transform: scale < 1 ? `scale(${scale})` : undefined,
        width: scaledWidth ? `${scaledWidth}px` : undefined,
      }}
    >
      <span
        className={clsx(prizeFundCampaignStyles.prizeTextStyle, prizeFundCampaignStyles.prizeTextStyleAmount)}
        data-variant={variant}
      >
        {formattedAmount}
      </span>
      <div
        className={`${prizeFundCampaignStyles.currency} ${prizeFundCampaignStyles.prizeTextStyle} ${showHasManyClass ? hasManyStyles.currency : ''}`}
        {...dataAttributes}
      >
        {!isCollapsed && !isCarouselVariant(variant) && <CampaignBadge variant={variant}  />}
        {isCollapsed || isCarouselVariant(variant) ? (
          <StringMapper className={prizeFundCampaignStyles.prizeTextStyle} text={currency} />
        ) : (
          currency
        )}
      </div>
    </div>
  );
};

export default memo(PrizeFundCampaign);
